import React, { useContext } from "react"
import parseDateTime from "../../../utils/parseDateTime"
import IInvoice from "../../../interfaces/IInvoice"
import IOrderPreview from "../../../interfaces/IOrderPreview"
import { formatMoneyFromCents } from "~/utils/formatMoney"
import ICamper from "../../../interfaces/ICamper"
import { formatName } from "~/utils/formatName"
import IPaymentMethods from "../../../interfaces/IPaymentMethods"
import { CreditCardIcon, EnvelopeIcon, UserCircleIcon } from "@heroicons/react/20/solid"
import { AdminContext } from "~/contexts/AdminContext"
import IOrder from "~/interfaces/IOrder"

interface ComponentProps {
  invoice: IInvoice | undefined
  nextChargedInvoice: IInvoice | undefined
  order: IOrder | undefined
  paymentMethod: IPaymentMethods | undefined
  camper: ICamper | undefined
}

const AutochargeConfirmationContent = (props: ComponentProps) => {
  if (!props.invoice && !props.nextChargedInvoice && !props.order) return <ChargeInformationCardSkeleton />

  const chargeDate = parseDateTime(props.invoice?.due_date)
  let chargeDateStr: string

  if (chargeDate?.isValid) {
    chargeDateStr = chargeDate.toFormat("LLL dd, yyyy")
  } else {
    chargeDateStr = ""
  }

  return (
    <div className="space-y-2">
      <p>This will enable autocharge for all future recurrences of this reservation.</p>
      <div className="">
        <p>The next charge will be:</p>
        <ChargeInformationCard
          camper={props.camper}
          order={(props.nextChargedInvoice?.order as IOrderPreview) || props.order}
          paymentMethod={props.paymentMethod}
          invoice={props.nextChargedInvoice || props.invoice}
        />
      </div>
    </div>
  )
}

const ChargeInformationCard = ({
  camper,
  order,
  paymentMethod,
  invoice,
}: Omit<ComponentProps, "nextChargedInvoice">) => {
  const { camp } = useContext(AdminContext)

  return (
    <div className="w-full mt-4">
      <h2 className="sr-only">Charge summary</h2>
      <div className="rounded-lg bg-gray-50 shadow-sm border border-gray-200">
        <dl className="flex flex-wrap">
          <div className="flex-auto pl-4 pt-4">
            <dt className="text-sm font-semibold leading-4 text-gray-500">Amount</dt>
            <dd className="mt-1 text-base font-semibold leading-4 text-gray-900">
              {formatMoneyFromCents(order?.unpaid_total_in_cents, camp?.currency)}
            </dd>
          </div>
          <div className="flex-none self-end px-4 pt-4">
            <dt className="text-sm font-semibold leading-4 text-gray-500">Charged on</dt>
            <dd className="mt-1 text-base font-semibold leading-4 text-gray-900">
              {parseDateTime(invoice?.due_date)?.toFormat("LLL dd, yyyy")}
            </dd>
          </div>
          <div className="mt-4 flex w-full flex-none gap-x-4 border-t border-gray-900/5 px-4 pt-4">
            <dt className="flex-none">
              <span className="sr-only">Guest</span>
              <UserCircleIcon aria-hidden="true" className="h-4 w-5 text-gray-500" />
            </dt>
            <dd className="font-medium leading-4 text-gray-500">{formatName(camper)}</dd>
          </div>
          <div className="mt-4 flex w-full flex-none gap-x-4 px-4">
            <dt className="flex-none">
              <span className="sr-only">Sent to</span>
              <EnvelopeIcon aria-hidden="true" className="h-4 w-5 text-gray-500" />
            </dt>
            <dd className="font-medium leading-4 text-gray-500">{invoice?.recipient_email || camper?.email}</dd>
          </div>
          <div className="my-4 flex w-full flex-none gap-x-4 px-4">
            <dt className="flex-none">
              <span className="sr-only">Status</span>
              <CreditCardIcon aria-hidden="true" className="h-4 w-5 text-gray-500" />
            </dt>
            <dd className="leading-4 text-gray-500">**** **** **** {paymentMethod?.last4}</dd>
          </div>
        </dl>
      </div>
    </div>
  )
}

const ChargeInformationCardSkeleton = () => {
  return (
    <div className="max-w-md mt-4">
      <h2 className="sr-only">Charge summary</h2>
      <div className="rounded-lg bg-gray-50 shadow-sm border border-gray-200">
        <dl className="flex flex-wrap">
          <div className="flex-auto pl-4 pt-4">
            <dt className="text-sm font-semibold leading-4 text-gray-500">Amount</dt>
            <dd className="mt-1 text-base font-semibold leading-4 text-gray-900">
              <div className="w-24 h-4 bg-gray-300 animate-pulse" />
            </dd>
          </div>
          <div className="flex-none self-end px-4 pt-4">
            <dt className="text-sm font-semibold leading-4 text-gray-500">Charged on</dt>
            <dd className="mt-1 text-base font-semibold leading-4 text-gray-900">
              <div className="w-32 h-4 bg-gray-300 animate-pulse" />
            </dd>
          </div>
          <div className="mt-4 flex w-full flex-none gap-x-4 border-t border-gray-900/5 px-4 pt-4">
            <dt className="flex-none">
              <div className="w-5 h-5 bg-gray-300 rounded-full animate-pulse" />
            </dt>
            <dd className="font-medium leading-4 text-gray-500">
              <div className="w-24 h-4 bg-gray-300 animate-pulse" />
            </dd>
          </div>
          <div className="mt-4 flex w-full flex-none gap-x-4 px-4">
            <dt className="flex-none">
              <div className="w-5 h-5 bg-gray-300 rounded-full animate-pulse" />
            </dt>
            <dd className="font-medium leading-4 text-gray-500">
              <div className="w-32 h-4 bg-gray-300 animate-pulse" />
            </dd>
          </div>
          <div className="my-4 flex w-full flex-none gap-x-4 px-4">
            <dt className="flex-none">
              <div className="w-5 h-5 bg-gray-300 rounded-full animate-pulse" />
            </dt>
            <dd className="leading-4 text-gray-500">
              <div className="w-32 h-4 bg-gray-300 animate-pulse" />
            </dd>
          </div>
        </dl>
      </div>
    </div>
  )
}

export default AutochargeConfirmationContent
